/**
 * Template parser orchestrator
 */

import type { GenerateCategoryTemplateResponse } from '../../data-store/copilotApi';
import type { Dispatch } from '@reduxjs/toolkit';

import parseConnectionStep from './connectionParser';
import parseGeneralStep from './generalParser';
import parseProvisioningStep from './provisioningParser';
import parseAccountStep from './accountParser';
import type { ParsedField, TemplateAttribute } from './types';

export interface ParsedTemplateData {
  General: ParsedField[];
  Connection: ParsedField[] & { instructions?: string };
  CreateAccount?: TemplateAttribute;
  UpdateAccount?: TemplateAttribute;
  EnableAccount?: TemplateAttribute;
  DisableAccount?: TemplateAttribute;
  RemoveAccount?: TemplateAttribute;
  AddAccess?: TemplateAttribute;
  RemoveAccess?: TemplateAttribute;
}

/**
 * Parse template response into step-specific data
 */
export const parseTemplateResponse = (
  templateData: GenerateCategoryTemplateResponse,
  isEditMode = false,
  dispatch?: Dispatch
): ParsedTemplateData => {
  const result: ParsedTemplateData = {
    General: [],
    Connection: [],
  };

  if (!templateData.configuration.groups) {
    return result;
  }

  templateData.configuration.groups.forEach((group) => {
    switch (group.name.toLowerCase()) {
      case 'general': {
        result.General = parseGeneralStep(
          group.attributes as TemplateAttribute[],
          isEditMode,
          dispatch
        );
        break;
      }

      case 'basic': {
        result.Connection = parseConnectionStep(
          templateData.configuration.groups,
          dispatch
        );
        break;
      }

      case 'account': {
        const provisioningData = parseProvisioningStep(
          group.attributes as TemplateAttribute[]
        );
        const accountData = parseAccountStep(
          group.attributes as TemplateAttribute[],
          isEditMode,
          dispatch
        );
        Object.assign(result, provisioningData, accountData);
        break;
      }

      default:
        break;
    }
  });

  return result;
};

export * from './types';
export { default as parseGeneralStep } from './generalParser';
export { default as parseConnectionStep } from './connectionParser';
export { default as parseProvisioningStep } from './provisioningParser';
export { default as parseAccountStep } from './accountParser';
